<script>
	import { labels } from "./utils/globals";
	import { onMount, onDestroy, tick } from "svelte";
	import { cubicOut } from "svelte/easing";
	import { tweened } from "svelte/motion";
	import { fade, fly, scale } from "svelte/transition";
	import { isDarkColor } from "./utils/utils";
	import { analytics } from "./utils/Analytics";

	export let data;
	export let offset;
	export let position;

	let artist;
	let circle = tweened(0, { duration: 500, easing: cubicOut });
	let isShowingStats = false;
	let id;
	let captionHeight = 0;
	let currentLabel = null;
	let currentPct = null;
	let isCurrentLabelDark;
	let mouse = { x: 0, y: 0 };
	let partner;
	let promise;
	let stats;
	let svg;
	let title;
	let total;
	let toggleStatsDelay = 3000;
	let toggleStatsTimeout;
	let url;

	$: css = position && `transform: translate(${position.x}px, ${position.y}px) translate(-50%, -50%);`;
	// because the selectedItem is constently updated (to get its position)
	// we need to safe-guard so that we don't reload the image each time...
	$: if (id !== data.id) {
		id = data.id;
		loadData();
	}

	onMount(() => {
		toggleStatsTimeout = setTimeout(() => {
			toggleStats();
		}, toggleStatsDelay);
	});

	onDestroy(() => {
		clearTimeout(toggleStatsTimeout);
	});

	function createPieSlice(pct, offset = 0, width = 1, height = 1) {
		const [startX, startY] = getCoordinatesFrom(offset);
		const [endX, endY] = getCoordinatesFrom(offset + pct);
		const largeArcFlag = pct > .5 ? 1 : 0;
		return [
			`M ${startX * width / 2} ${startY * height / 2}`,
			`A ${width / 2} ${height / 2} 0 ${largeArcFlag} 1 ${endX * width / 2} ${endY * height / 2}`,
			`L 0 0`
		].join(" ");
	}

	function getCoordinatesFrom(pct) {
		return [Math.cos(2 * Math.PI * pct), Math.sin(2 * Math.PI * pct)];
	}

	function hideLabel() {
		currentLabel = null;
		currentPct = null;
	}

	async function loadData() {
		if (isShowingStats) {
			toggleStats();
		}
		promise = preload(data.url + "-v2");
		url = data["GAC_url"];
		title = data.attribution[0];
		artist = data.attribution[1];
		partner = data.attribution[2];
		total = data.stats.reduce((acc, stat) => acc + stat.val, 0);
		stats = data.stats.map((stat) => {
			return {
				pct: stat.val / total,
				label: stat.cat,
				ref: null
			}
		});
		stats.forEach((stat, i) => {
			stat.off = stats.reduce((acc, s, j) => acc + (i >= j ? 0 : s.pct), 0);
		});
	}

	function preload(src) {
		return new Promise((resolve) => {
			const image = new Image();
			image.addEventListener("load", function remove() {
				image.removeEventListener("load", remove);
				resolve({
					src,
					width: image.naturalWidth,
					height: image.naturalHeight
				});
			});
			image.src = src;
		});
	}

	function showLabel(label, pct) {
		if (!isShowingStats) return;
		currentLabel = label;
		currentPct = pct;
		isCurrentLabelDark = isDarkColor($labels[label].color);
	}

	function gacLinkClick(){
		const bits = url.split('/')
		analytics.clickGacLink(bits[bits.length - 1])
	}

	async function toggleStats() {
		clearTimeout(toggleStatsTimeout);
		isShowingStats = !isShowingStats;
		if (!isShowingStats) {
			hideLabel();
		} else {
			
			// had to add this 
			if (svg == null) return;

			const bbox = svg.getBoundingClientRect();
			const paths = svg.querySelectorAll("path");
			let totalPct = 0;
			for (let i = 0; i < paths.length; i++) {
				const pathBbox = paths[i].getBoundingClientRect();
				const area = {
					top: pathBbox.top,
					right: pathBbox.right,
					bottom: pathBbox.bottom,
					left: pathBbox.left
				};
				if (area.top < bbox.top) area.top = bbox.top;
				if (area.right > bbox.right) area.right = bbox.right;
				if (area.bottom > bbox.bottom) area.bottom = bbox.bottom;
				if (area.left < bbox.left) area.left = bbox.left;
				let align = "center";
				let justify = "center";
				let text = "center";
				if (paths.length === 2) {
					if (i === 0) {
						align = "flex-end";
					} else {
						align = "flex-start";
					}
					if (stats[i].pct < 0.4) {
						justify = "flex-end";
						text = "right";
					}
				} else if (paths.length === 3) {
					if (i === 0) {
						align = "flex-end";
						if (stats[i].pct < 0.4) {
							justify = "flex-end";
							text = "right";
						}
					} else if (i === 1) {
						if (totalPct > 0.4) {
							align = "flex-start";
						}
						justify = "flex-start";
						text = "left";
					} else if (i === 2) {
						if (stats[i].pct < 0.25) {
							if (stats[i].pct > 0.15 && (area.right - area.left) / (area.bottom - area.top) > 1) {
								align = "flex-start";
							} else {
								align = "flex-end";
							}
						} else {
							align = "flex-start";
						}
						justify = "flex-end";
						text = "right";
					}
				}
				stats[i].area = {
					...area,
					align,
					justify,
					text
				}
				totalPct += stats[i].pct;
			}
		}

		const bits = url.split('/')
		analytics.toggleStats(bits[bits.length - 1])
	
	}

	function updateMouse(e) {
		mouse.x = e.pageX;
		mouse.y = e.pageY;
	}
</script>

<svelte:window on:resize={() => {
	if (isShowingStats) {
		toggleStats();
	}
}} />

<div class="asset" class:offset style={css} out:fade={{ duration: 500 }}>
	{#await promise then { src, width, height }}
		<figure class:portrait={width / height < 0.8}
			class:square={width / height >= 0.8 && width / height <= 1.2}
			class:landscape={width / height > 1.2}
			in:scale={{ duration: 500, easing: cubicOut }}>
			<div class="image"
				style={`max-height: calc(100% - ${captionHeight}px);`}>
				<img {src} {width} {height} on:click={toggleStats} />
				<svg xmlns="http://www.w3.org/2000/svg"
					preserveAspectRatio="xMidYMid slice"
					{width} {height}
					viewBox="-0.35 -0.35 0.70 0.70"
					bind:this={svg}
					class:no-cursor={isShowingStats && currentLabel !== null}
					class:show={isShowingStats}
					on:click={toggleStats}
					on:mousemove={updateMouse}
					on:mouseleave={hideLabel}>
					<!-- <defs>
						<clipPath id="circle">
							<circle cx="0" cy="0" r={$circle} />
						</clipPath>
					</defs> -->
					<g transform="scale(1, -1)">
						{#each stats as { pct, off, label, ref }}
							<path d="{createPieSlice(pct, off)}"
								fill={$labels[label].color}
								on:mouseenter={showLabel(label, pct)} />
						{/each}
					</g>
				</svg>
			</div>
			<figcaption bind:offsetHeight={captionHeight}>
				<a href={url}
					target="_blank"
					title="Check this artwork on Google Arts &amp; Culture"
					style={`--color: ${$labels[stats[0].label].color}`}
					in:fly={{
						y: 20,
						duration: 500,
						easing: cubicOut,
						delay: 500
					}} 
					on:click={gacLinkClick}>{title}</a>
				<h3 in:fly={{
					y: 20,
					duration: 500,
					easing: cubicOut,
					delay: 600 }}>{artist}</h3>
				<h4 in:fly={{
					y: 20,
					duration: 500,
					easing: cubicOut,
					delay: 700 }}>{partner}</h4>
				<svg xmlns="http://www.w3.org/2000/svg"
					width="60"
					height="60"
					viewBox="-0.5 -0.5 1 1"
					in:fly={{ y: 20, duration: 500, easing: cubicOut, delay: 800 }}
					on:click={toggleStats}>
					<defs>
						<clipPath id="circle">
							<circle cx="0" cy="0" r="0.5" />
						</clipPath>
					</defs>
					<g transform="scale(1, -1)">
						{#each stats as { pct, off, label }}
							<path d="{createPieSlice(pct, off)}" fill={$labels[label].color} />
						{/each}
					</g>
					<image
						x="-0.5"
						y="-0.5"
						width="1"
						height="1"
						xlink:href={src.replace("=s1200", "=s120")}
						clip-path="url(#circle)"
						preserveAspectRatio="xMidYMid slice"
						class:show={isShowingStats}

					></image>
				</svg>
			</figcaption>
		</figure>
	{/await}
</div>

{#if isShowingStats && currentLabel !== null}
	<p transition:fade={{ duration: 150 }} class:light={isCurrentLabelDark}
		style="transform: translate({mouse.x}px, {mouse.y}px) translate(-50%, -50%); text-align: center;">{Math.floor(currentPct * 100)}%<br>{@html $labels[currentLabel].text.replace(", ", "<br>")}</p>
{/if}

{#if isShowingStats}
	{#each stats as { label, pct, area}}
		{#if currentLabel !== label}
		<p transition:fade={{ duration: 150 }} class:light={isDarkColor($labels[label].color)}
			style="width: {area.right - area.left}px; height: {area.bottom - area.top}px; align-items: {area.align}; justify-content: {area.justify}; transform: translate({area.left}px, {area.top}px); text-align: {area.text};">{Math.floor(pct * 100)}%<br>{@html $labels[label].text.replace(", ", "<br>")}</p>
		{/if}
	{/each}
{/if}

<style>
	.asset {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		position: absolute;
		top: 0;
		left: 0;
		z-index: 10;
		text-align: center;
		user-select: none;
		pointer-events: none;
	}

	.asset.offset {
		width: calc(100% - 13rem);
	}
	
	.asset>figure {
		display: flex;
		align-content: center;
		flex-wrap: wrap;
		justify-content: center;
		width: 100%;
		height: 100%;
		/* max-width: 760px; */
		/* max-height: 760px; */
		padding: 1.5rem 1.5rem 1.1rem;
	}

	.asset>figure.portrait,
	.asset>figure.square {
		height: 100%;
	}

	.image {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		position: relative;
	}

	.image>img {
		display: block;
		width: auto;
		height: auto;
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
		object-position: center;
	}

	.image>svg {
		display: block;
		width: auto;
		height: auto;
		max-width: 100%;
		max-height: 100%;
		position: absolute;
		top: 50%;
		left: 50%;
		z-index: 10;
		transform: translate3d(-50%, -50%, 0);
		opacity: 0;
		/* visibility: hidden; */
		transition: transform 0.5s cubic-bezier(0.1, 0.5, 0.4, 1),
			opacity 0.5s cubic-bezier(0.1, 0.5, 0.4, 1),
			visibility 0s 0.5s;
		pointer-events: all;
		cursor: pointer;
	}

	.image>svg.no-cursor {
		cursor: none;
	}
	
	.image>svg.show {
		transform: translate3d(-50%, -50%, 0);
		opacity: 1;
		/* visibility: visible; */
		transition-delay: 0s, 0s, 0s;
	}

	figcaption {
		max-width: calc(660px + 2em);
		padding: 1em calc(60px + 2em) 0 0;
		position: relative;
		text-align: left;
		/* text-shadow: 0 0 5px white, 0 0 20px white; */
	}

	figcaption>svg {
		position: absolute;
		top: 50%;
		right: 0;
		transform: translateY(0.5em) translateY(-50%);
		pointer-events: all;
		cursor: pointer;
	}

	figcaption>svg>image {
		opacity: 0;
		visibility: hidden;
		transition: transform 0.5s cubic-bezier(0.1, 0.5, 0.4, 1),
			opacity 0.5s cubic-bezier(0.1, 0.5, 0.4, 1),
			visibility 0s 0.5s;
	}
	
	figcaption>svg>image.show {
		opacity: 1;
		visibility: visible;
		transition-delay: 0s, 0s, 0s;
	}

	a {
		display: block;
		color: inherit;
		font-size: 1.5em;
		font-weight: 500;
		line-height: 1.4;
		text-decoration: none;
		pointer-events: all;
		transition: color 0.15s ease-in-out;
	}

	a::after {
		content: "\2197";
    margin-left: 0.3em;
    font-size: 1rem;
    vertical-align: top;
	}

	a:hover {
		color: var(--color);
	}

	a+* {
		margin-top: 0.2em;
	}

	h3,
	h4 {
		margin-top: 0.4em;
		font-size: 1em;
		font-weight: normal;
		line-height: 1.2;
	}

	p {
		display: flex;
		padding: 0.7em 1em;
		position: absolute;
		top: 0;
		left: 0;
		z-index: 100;
		font-size: 1.5em;
		font-weight: 500;
		/* line-height: 1; */
		pointer-events: none;
	}

	p.light {
		color: white;
	}

	path {
		stroke-width: 0;
	}

	@media (max-width: 960px) {
		.asset.offset {
			width: calc(100% - 8rem);
		}
	}

	@media (max-width: 800px) {
		.asset.offset {
			width: 100%;
			height: calc(100% - 2rem);
			top: -1rem;
		}

		figcaption {
			padding-right: calc(40px + 1em);
		}

		figcaption>svg {
			width: 40px;
			height: 40px;
		}

		a {
			font-size: 1.2em;
		}

		p {
			font-size: 1.2em;
		}
	}

	@media (max-width: 480px) {
		.asset>figure {
			padding: 1rem;	
		}

		figcaption { 
			width: 100%;
		}
	}

	@media (max-height: 600px) and (orientation: landscape) {
		.asset.offset {
			width: calc(100% - 8rem);
			height: 100%;
			top: 0;
		}

		p {
			padding: 0.5em 0.8em;
			font-size: 1em;
		}
	}
</style>